/** 来源资格独立控制台：真实声明/证据/核验状态，不以资格取代收入发布状态。 */
import { DeclareUsdCoverageRequestSchema, type UsdCoverageResponse } from '@novel-admin/shared';
import { useState } from 'react';
import { AdminApiError } from '~/api/errors';
import { useAppScope } from '~/app/app-scope-store';
import { useApps } from '~/data/queries';
import {
  assertCoverageOnline,
  useUsdCoverage,
  useUsdCoverageWrite,
} from '~/hooks/use-usd-coverage';
import { useAuthStore } from '~/stores/auth-store';
import { Dialog, DialogBody } from '~/ui/Dialog';
import { FormRow } from '~/ui/FormRow';
import { Pagination } from '~/ui/Pagination';
import { TableSkeleton } from '~/ui/Skeleton';
import { Table } from '~/ui/Table';
import { toast } from '~/ui/Toast';

type Operation = {
  appId: string;
  operation: 'declare' | 'verify' | 'revoke';
  coveredSince?: string;
};
function Proof({ row }: { row: UsdCoverageResponse }) {
  const proof = row.qualification_evidence;
  if (!proof) return <span className="muted">尚无完整核验证据</span>;
  return (
    <details>
      <summary>
        {proof.checks.source_configuration.passed &&
        proof.checks.read_permissions.passed &&
        proof.checks.history_completeness.passed
          ? '完整核验通过'
          : '完整核验未通过'}
      </summary>
      <div>
        来源：{proof.source === 'WEB_STRIPE' ? 'Stripe' : 'Adapty S3'} · 采集：{proof.captured_at}
      </div>
      <div>
        完整历史窗口：{proof.covered_since} 至 {proof.checked_until}（不含）
      </div>
      {Object.entries(proof.checks).map(([key, value]) => (
        <div key={key}>
          {{
            source_configuration: '来源配置',
            read_permissions: '只读权限',
            history_completeness: '历史完整性',
          }[key] ?? key}
          ：{value.passed ? '通过' : '未通过'}
        </div>
      ))}
      <div style={{ overflowWrap: 'anywhere' }}>证据摘要：{proof.evidence_digest}</div>
    </details>
  );
}
export default function MetricsUsdCoveragePage() {
  const appId = useAppScope((s) => s.appId);
  const root = useAuthStore((s) => s.user?.role) === 'root';
  const apps = useApps();
  const [page, setPage] = useState(1);
  const [scope, setScope] = useState(appId);
  const [operation, setOperation] = useState<Operation | null>(null);
  if (scope !== appId) {
    setScope(appId);
    setPage(1);
    setOperation(null);
  }
  const query = useUsdCoverage(appId, page);
  const write = useUsdCoverageWrite();
  const [date, setDate] = useState('');
  const activeOperation = operation && operation.appId === appId ? operation : null;
  const names = new Map(apps.data.map((app) => [app.id, app.name]));
  const unavailable = query.error instanceof AdminApiError && query.error.httpStatus === 404;
  function open(value: Operation) {
    setDate(value.coveredSince ?? '');
    setOperation(value);
  }
  async function submit() {
    if (!activeOperation) return;
    const input = { ...activeOperation, coveredSince: date };
    try {
      assertCoverageOnline();
      if (input.operation === 'declare')
        DeclareUsdCoverageRequestSchema.parse({ covered_since: date });
      const row = await write.mutateAsync(input);
      setOperation(null);
      toast(
        input.operation === 'verify'
          ? row.verified_at
            ? '真实来源资格核验通过；收入发布状态请查看指标页'
            : '已完整核验，当前来源尚未满足资格'
          : input.operation === 'revoke'
            ? '来源资格已撤销'
            : '历史覆盖起点已保存',
        { tone: row.verified_at || input.operation !== 'verify' ? 'pos' : 'warn' },
      );
    } catch (error) {
      toast(error instanceof Error ? error.message : '操作失败，请刷新核实当前状态', {
        tone: 'neg',
      });
    }
  }
  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">USD 来源资格</h1>
          <p className="muted">
            登记真实收入历史覆盖范围，查询完整核验证据。资格通过后仍须等待收入快照发布。
          </p>
        </div>
        {root && (
          <button
            type="button"
            className="btn btn-primary"
            disabled={appId === 'all' || query.isError || write.isPending}
            onClick={() =>
              open({
                appId,
                operation: 'declare',
                coveredSince: query.data?.data.find((row) => row.app_id === appId)?.covered_since,
              })
            }
          >
            登记 / 修改覆盖起点
          </button>
        )}
      </div>
      {appId === 'all' && <p className="muted">请选择具体 App 后登记、核验或撤销来源资格。</p>}
      {query.isLoading ? (
        <TableSkeleton />
      ) : query.isError ? (
        <div role="alert">
          {unavailable ? '服务更新中，请稍后重试' : query.error.message}
          <button type="button" className="btn" onClick={() => void query.refetch()}>
            重新加载
          </button>
        </div>
      ) : (
        <>
          <div className="card" style={{ overflowX: 'auto' }}>
            <Table className="tbl">
              <thead>
                <tr>
                  <th>App</th>
                  <th>历史覆盖起点（UTC）</th>
                  <th>来源资格</th>
                  <th>完整核验证据</th>
                  {root && <th>操作</th>}
                </tr>
              </thead>
              <tbody>
                {query.data?.data.map((row) => (
                  <tr key={row.app_id}>
                    <td>{names.get(row.app_id) ?? row.app_id}</td>
                    <td>{row.covered_since}</td>
                    <td>
                      {row.verified_at ? '已核验' : '待核验'}
                      {row.verified_at && <div className="muted">{row.verified_at}</div>}
                    </td>
                    <td>
                      <Proof row={row} />
                    </td>
                    {root && (
                      <td>
                        <button
                          type="button"
                          className="btn"
                          disabled={appId === 'all' || write.isPending}
                          onClick={() => open({ appId: row.app_id, operation: 'verify' })}
                        >
                          重新核验
                        </button>
                        <button
                          type="button"
                          className="btn"
                          disabled={appId === 'all' || write.isPending}
                          onClick={() => open({ appId: row.app_id, operation: 'revoke' })}
                        >
                          撤销
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
                {query.data?.data.length === 0 && (
                  <tr>
                    <td colSpan={root ? 5 : 4}>尚无覆盖声明</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
          <Pagination total={query.data?.total ?? 0} page={page} pageSize={50} onChange={setPage} />
        </>
      )}
      {activeOperation && (
        <Dialog
          title={
            activeOperation.operation === 'declare'
              ? '登记 / 修改覆盖起点'
              : activeOperation.operation === 'verify'
                ? '核验真实来源资格'
                : '撤销来源资格'
          }
          onClose={() => {
            if (!write.isPending) setOperation(null);
          }}
          footer={
            <>
              <button
                type="button"
                className="btn"
                disabled={write.isPending}
                onClick={() => setOperation(null)}
              >
                取消
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={write.isPending}
                onClick={() => void submit()}
              >
                {write.isPending ? '处理中…' : '确认'}
              </button>
            </>
          }
        >
          <DialogBody>
            <p>App：{names.get(activeOperation.appId) ?? activeOperation.appId}</p>
            {activeOperation.operation === 'declare' ? (
              <FormRow
                label="真实历史覆盖起点（UTC）"
                required
                hint="修改起点会清除已有资格与证据，须重新核验。"
              >
                <input
                  className="input"
                  aria-label="真实历史覆盖起点"
                  type="date"
                  value={date}
                  onChange={(event) => setDate(event.target.value)}
                />
              </FormRow>
            ) : (
              <p>
                {activeOperation.operation === 'verify'
                  ? '系统将只读采集供应商权限和完整历史证据；证据不完整时保持待核验。原生历史指标恢复在后台执行。'
                  : '撤销后来源不再具有收入发布资格；重新登记必须再次核验。'}
              </p>
            )}
          </DialogBody>
        </Dialog>
      )}
    </div>
  );
}
